import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlatformBanner } from "@/components/PlatformBanner";
import { PaymentGraceBanner } from "@/components/PaymentGraceBanner";
import { GetTheAppCard } from "@/components/AppStoreBadges";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Trophy, 
  Star, 
  Target, 
  Book, 
  Calculator, 
  Atom, 
  Globe, 
  PenTool,
  Crown,
  Medal,
  Award,
  Zap,
  Flame,
  Brain,
  Users,
  TrendingUp,
  Rocket,
  User,
  Shield,
  CheckCircle,
  Heart,
  BookOpen,
  Beaker,
  Globe2,
  Lightbulb,
  Timer,
  UserCheck,
  LogOut,
  Triangle,
  BarChart3,
  Plus,
  Shuffle,
  CreditCard,
  AlertCircle,
  Gift,
  Check,
  Sparkles,
  X,
  Lock,
  Camera,
  MessageSquare,
  MessageCircle,
  UserPlus,
  Clock as ClockIcon,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  Minus as MinusIcon,
  Circle,
  HelpCircle,
  ChevronDown,
  Video as VideoIcon,
  Compass,
  ChevronRight, ArrowRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ALL_BADGES } from "@/lib/badges";
import { BadgeArt } from "@/components/BadgeArt";
import { TileBackdrop, type TileTheme } from "@/components/TileBackdrop";
import { PerformanceTaskTile } from "@/components/PerformanceTaskTile";
import { PepCountdownChip } from "@/components/PepCountdownChip";
import { usePepWindows, countdownForSubject } from "@/lib/pepCountdown";
import { useAuth } from "@/contexts/AuthContext";
import { examStageForGrade } from "@shared/territories";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { RecommendationsPayload } from "@/lib/studentCourses";
import { getTopicMetadata } from "@/lib/topicMetadata";
import { Illustration } from "@/components/Illustration";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { QuickStartGuide } from "@/components/QuickStartGuide";
import { computeLevel, levelInfoFromStored } from "@shared/xp";
import { ProfilePhotoViewer } from "@/components/ProfilePhotoViewer";
import { OnlineDot } from "@/components/OnlineDot";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

function getDashboardTrend(inactiveDays: number, activityFactor: number): {
  direction: "rising" | "steady" | "slipping" | "falling" | "critical";
  label: string;
  tooltip: string;
  arrowColor: string;
  bgColor: string;
} {
  if (inactiveDays === 0) return { direction: "rising", label: "Rising", tooltip: "Active today", arrowColor: "text-green-600 dark:text-green-400", bgColor: "bg-green-500/10" };
  if (inactiveDays <= 3) return { direction: "steady", label: "Steady", tooltip: `${inactiveDays}d ago - grace period`, arrowColor: "text-green-500 dark:text-green-400", bgColor: "bg-green-500/10" };
  if (activityFactor >= 0.9) return { direction: "slipping", label: "Slipping", tooltip: `${inactiveDays}d inactive, -2%/day`, arrowColor: "text-amber-500 dark:text-amber-400", bgColor: "bg-amber-500/10" };
  if (activityFactor >= 0.6) return { direction: "falling", label: "Falling", tooltip: `${inactiveDays}d inactive, -${inactiveDays <= 14 ? '3' : '5'}%/day`, arrowColor: "text-orange-500 dark:text-orange-400", bgColor: "bg-orange-500/10" };
  return { direction: "critical", label: "Freefall", tooltip: `${inactiveDays}d inactive, ${Math.round((1 - activityFactor) * 100)}% lost`, arrowColor: "text-red-500 dark:text-red-400", bgColor: "bg-red-500/10" };
}

function TermProgressCard() {
  const { data } = useQuery({
    queryKey: ["/api/curriculum/current-term"],
    queryFn: async () => {
      const res = await fetch("/api/curriculum/current-term");
      if (!res.ok) return null;
      return res.json();
    },
  });

  if (!data?.term) return null;

  const allTopics = Object.values(data.topics as Record<string, any[]>).flat();
  const subjects = Object.keys(data.topics || {});

  return (
    <Card className="hover-elevate">
      <CardContent className="p-3 md:p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-sm">{data.term.name} Focus</p>
            <p className="text-xs text-muted-foreground">{allTopics.length} topics across {subjects.length} subjects this term</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {subjects.slice(0, 3).map((subject: string) => {
            const topics = (data.topics as Record<string, any[]>)[subject];
            const totalQ = topics.reduce((s: number, t: any) => s + (t.questionCount || 0), 0);
            return (
              <div key={subject} className="bg-muted/50 rounded-lg p-2">
                <p className="text-xs font-medium truncate">{subject}</p>
                <p className="text-[10px] text-muted-foreground">{topics.length} topic{topics.length > 1 ? 's' : ''}, {totalQ} questions</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardTrendArrow({ trend }: { trend: ReturnType<typeof getDashboardTrend> }) {
  // Activity status shown as a presence dot (green active, amber idle, red gone quiet).
  const pulse = trend.direction === "critical" || trend.direction === "falling" ? "animate-pulse" : "";
  return <div className={`flex items-center justify-center rounded-full p-1 ${trend.bgColor} ${pulse}`}><Circle className={`w-2 h-2 ${trend.arrowColor} fill-current`} strokeWidth={0} /></div>;
}

// Renders only when the summer classes feature flag is on. Polls the public
// /api/feature-flags endpoint and hides itself entirely otherwise.
//
// Visual treatment is intentionally loud: solid red, white type, pulsing
// LIVE dot: so students notice it on a dashboard with a lot going on.
/**
 * One small "For you" tile. Shows the top interest-course picks, or nudges the student
 * to tell us what they love when they have not set interests yet.
 */
function ForYouTile() {
  const [, setLocation] = useLocation();
  const { data } = useQuery<RecommendationsPayload>({
    queryKey: ["/api/recommendations"],
    staleTime: 60_000,
  });

  if (!data || !data.interestsSet) return null;
  const picks = data.forYou.slice(0, 3);
  if (picks.length === 0) return null;

  return (
    <div
      className="group relative overflow-hidden rounded-xl border border-[#2e1a5e] bg-[#2e1a5e] text-white p-4 md:p-5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      data-testid="card-for-you"
    >
      <TileBackdrop theme="courses" className="text-[#ffd66b]" opacity={0.35} />
      <div className="tile-shine" />
      {/* Night-sky dots, same pattern as the header band */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1.4px)', backgroundSize: '18px 18px' }} />
      <div className="absolute -right-4 -bottom-5 opacity-20 pointer-events-none"><Illustration name="owl-rocket" size={120} /></div>
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#ffd66b] text-[#2e1a5e] flex items-center justify-center flex-shrink-0 -rotate-3 shadow-sm">
            <Compass className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-base md:text-lg leading-tight">Courses for you</h3>
            <p className="text-xs font-medium text-white/70">Picked from what you love</p>
          </div>
          <button type="button" onClick={() => setLocation("/courses")} className="text-xs font-semibold text-[#ffd66b] hover:underline flex-shrink-0" data-testid="button-for-you-all">See all</button>
        </div>

        <div className="mt-3 space-y-1.5">
          {picks.map((pick, i) => (
            <button
              key={pick.course.id}
              type="button"
              onClick={() => setLocation(`/courses/${pick.course.slug}`)}
              className="group w-full flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/15 transition-colors text-left"
              data-testid={`row-for-you-${pick.course.slug}`}
            >
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${["bg-[#bfe0ff]", "bg-[#b9ecd2]", "bg-[#ffb3a7]"][i % 3]} ${["rotate-2", "-rotate-2", "rotate-1"][i % 3]}`} aria-hidden="true">
                {pick.course.emoji ? <span className="text-lg leading-none">{pick.course.emoji}</span> : <BookOpen className="w-4 h-4 text-[#2e1a5e]" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold truncate">{pick.course.title}</span>
                <span className="block text-xs text-white/65 truncate">
                  {pick.reasons[0] || `${pick.course.lessonCount} lessons · ${pick.course.xpTotal} XP`}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-white/50 group-hover:translate-x-0.5 group-hover:text-white transition-all flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummerClassesTile() {
  const [, setLocation] = useLocation();
  const { data: flags } = useQuery<{ summerClasses: boolean }>({
    queryKey: ["/api/feature-flags"],
    queryFn: async () => (await fetch("/api/feature-flags", { credentials: "include" })).json(),
    staleTime: 60_000,
  });
  if (!flags?.summerClasses) return null;
  return (
    <Card
      className="rounded-xl cursor-pointer bg-red-600 border-red-700 text-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      onClick={() => setLocation("/classes")}
      data-testid="card-live-classes"
    >
      <CardContent className="p-4 md:p-5 flex items-center gap-3 md:gap-4">
        <div className="w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
          <VideoIcon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-white/95">Live</span>
          </div>
          <p className="font-bold text-base md:text-lg leading-tight">Live Classes</p>
          <p className="text-xs md:text-sm text-white/90">Summer 2026. Register, join live, take post-class quizzes.</p>
        </div>
        <Button
          size="sm"
          className="flex-shrink-0 rounded-lg bg-white text-red-700 hover:bg-white/90 font-semibold"
        >
          View Classes
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Subject colour is a tint on a 40px icon square, plus the bar at full strength.
 * Classes are literal so Tailwind's scanner keeps them. See scripts/design/DESIGN_SYSTEM.md.
 */
// Flat but colourful: each subject owns its colour on the card, the icon sticker, the bar and the button.
const SUBJECT_THEME: Record<string, TileTheme> = { "math": "math", "english": "english", "science": "science", "social studies": "social", "ability test": "ability" };
const SUBJECT_DOODLE: Record<string, string> = { "math": "text-blue-700", "english": "text-emerald-700", "science": "text-orange-700", "social studies": "text-violet-700", "ability test": "text-rose-700" };
const STICKER_ROT = ["-3deg", "2deg", "-2deg", "3deg", "-1deg"];
const SUBJECT_STYLES: Record<string, { card: string; sticker: string; chip: string; bar: string; track: string; button: string; text: string }> = {
  "math": { card: "bg-blue-500/10 border-blue-500/25", sticker: "bg-blue-500 text-white", chip: "bg-blue-500/15 text-blue-800 dark:text-blue-200", bar: "bg-blue-500", track: "bg-blue-500/15", button: "bg-blue-500 hover:bg-blue-600 text-white", text: "text-blue-900/70 dark:text-blue-200/80" },
  "english": { card: "bg-emerald-500/10 border-emerald-500/25", sticker: "bg-emerald-500 text-white", chip: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-200", bar: "bg-emerald-500", track: "bg-emerald-500/15", button: "bg-emerald-500 hover:bg-emerald-600 text-white", text: "text-emerald-900/70 dark:text-emerald-200/80" },
  "science": { card: "bg-orange-500/10 border-orange-500/25", sticker: "bg-orange-500 text-white", chip: "bg-orange-500/15 text-orange-800 dark:text-orange-200", bar: "bg-orange-500", track: "bg-orange-500/15", button: "bg-orange-500 hover:bg-orange-600 text-white", text: "text-orange-900/70 dark:text-orange-200/80" },
  "social studies": { card: "bg-violet-500/10 border-violet-500/25", sticker: "bg-violet-500 text-white", chip: "bg-violet-500/15 text-violet-800 dark:text-violet-200", bar: "bg-violet-500", track: "bg-violet-500/15", button: "bg-violet-500 hover:bg-violet-600 text-white", text: "text-violet-900/70 dark:text-violet-200/80" },
  "ability test": { card: "bg-rose-500/10 border-rose-500/25", sticker: "bg-rose-500 text-white", chip: "bg-rose-500/15 text-rose-800 dark:text-rose-200", bar: "bg-rose-500", track: "bg-rose-500/15", button: "bg-rose-500 hover:bg-rose-600 text-white", text: "text-rose-900/70 dark:text-rose-200/80" },
};
const DEFAULT_SUBJECT_STYLE = { card: "bg-primary/10 border-primary/25", sticker: "bg-primary text-primary-foreground", chip: "bg-primary/15 text-primary", bar: "bg-primary", track: "bg-primary/15", button: "bg-primary hover:bg-primary/90 text-primary-foreground", text: "text-primary/80" };
const STICKER_TILT = ["-rotate-3", "rotate-2", "-rotate-2", "rotate-3", "-rotate-1"];

const Dashboard = () => {
  const { user, isAuthenticated, logout, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(0);
  const [showLeaderboardInfo, setShowLeaderboardInfo] = useState(false);
  const [dashLbMode, setDashLbMode] = useState<'weekly' | 'academy'>('weekly');
  const [viewingPhoto, setViewingPhoto] = useState<{ url: string; name: string } | null>(null);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, authLoading, setLocation]);
  
  // Fetch user stats and progress
  const { data: dashboardStats, isLoading: statsLoading } = useQuery<{
    totalPoints: number;
    totalCorrect: number;
    totalQuestions: number;
    accuracy: number;
    achievementCount: number;
    streakDays: number;
    topicProgress: any[];
    subjectBreakdown?: any[];
    currentLevel?: number;
    currentXP?: number;
    nextLevelXP?: number;
  }>({
    queryKey: ['/api/dashboard/stats'],
    enabled: isAuthenticated && !!user,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery<{
    entries?: any[];
  }>({
    queryKey: ['/api/leaderboard/weekly'],
    enabled: isAuthenticated && !!user && dashLbMode === 'weekly',
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { data: academyLbData } = useQuery<{ entries?: any[] }>({
    queryKey: ['/api/leaderboard/academy', { page: 1, limit: 10 }],
    queryFn: async () => {
      const res = await fetch('/api/leaderboard/academy?page=1&limit=10', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: isAuthenticated && !!user && dashLbMode === 'academy',
  });

  const { data: userAchievements, isLoading: achievementsLoading } = useQuery<any[]>({
    queryKey: ['/api/achievements'],
    enabled: isAuthenticated && !!user,
  });

  // Fetch prize eligibility for banner
  const { data: prizeData } = useQuery<{ eligible?: boolean; prizeAmountJMD?: number; merchantName?: string | null; milestone?: number; testsCompleted?: number; nextMilestone?: number | null; tiers?: Array<{ milestone: number; prizeAmountJMD: number; winProbability: number; minAvgScore: number | null; merchantName: string | null }> }>({
    queryKey: ['/api/prizes/check'],
    enabled: isAuthenticated && !!user,
  });

  // Fetch campaign wins for banner
  const { data: campaignWins = [] } = useQuery<any[]>({
    queryKey: ['/api/prizes/campaign-wins'],
    enabled: isAuthenticated && !!user,
    retry: 1
  });

  // Fetch weekly stats
  const { data: weeklyStats } = useQuery<{
    daysActive: number;
    currentStreak: number;
    weeklyXP: number;
  }>({
    queryKey: ['/api/dashboard/weekly-stats'],
    enabled: isAuthenticated && !!user,
  });

  // Fetch friends for Online Now widget
  const { data: friendConnections } = useQuery<{
    followers: Array<{ id: string; firstName: string; lastName: string; profilePhotoUrl?: string }>;
    following: Array<{ id: string; firstName: string; lastName: string; profilePhotoUrl?: string }>;
  }>({
    queryKey: ["/api/social/connections", user?.id],
    enabled: isAuthenticated && !!user?.id,
  });
  const allFriends = [...(friendConnections?.following ?? []), ...(friendConnections?.followers ?? [])]
    .filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i); // dedupe
  const { isOnline } = useOnlineStatus(allFriends.map(f => f.id));
  const onlineFriends = allFriends.filter(f => isOnline(f.id));

  // Fetch reading stats
  const { data: readingStats } = useQuery<{
    totalMaterials: number;
    completed: number;
    inProgress: number;
    totalTimeSeconds: number;
  }>({
    queryKey: ['/api/reading/stats'],
    enabled: isAuthenticated && !!user,
  });

  // Fetch interactive lesson stats
  const { data: lessonStats } = useQuery<{
    total: number;
    completed: number;
    inProgress: number;
    xpEarned: number;
  }>({
    queryKey: ['/api/lessons/stats'],
    enabled: isAuthenticated && !!user,
  });

  // The lessons themselves, so the card can lead with the one to open next rather than with three
  // counters. A student cannot act on "0 In Progress"; they can act on a titled lesson.
  const { data: lessonList = [] } = useQuery<Array<{
    slug: string; title: string; subject: string; topic: string;
    estimatedMinutes: number; xpReward: number;
    progress?: { status: string } | null;
  }>>({
    queryKey: ['/api/lessons'],
    enabled: isAuthenticated && !!user,
  });

  // Resume beats start, and start beats nothing.
  const nextLesson =
    lessonList.find(l => l.progress?.status === "in_progress") ??
    lessonList.find(l => !l.progress || l.progress.status === "not_started") ??
    null;
  const resuming = nextLesson?.progress?.status === "in_progress";

  // What this grade is actually working towards. Everything exam-specific below gates on this
  // rather than on a grade range, because clamping an out-of-range grade back to 6 is exactly how
  // a Grade 11 student was being shown PEP Performance Tasks and a PEP countdown.
  const examStage = examStageForGrade(user?.grade);
  const isPrimaryExam = examStage === 'PRIMARY';

  // Live primary-exam dates for this grade, shown as a countdown on every subject tile.
  const { data: pepWindows } = usePepWindows(
    isPrimaryExam ? Number(user?.grade) : 6,
    isAuthenticated && !!user && isPrimaryExam,
  );

  // Fetch subjects dynamically based on user's grade
  const { data: subjectsData = [] } = useQuery<Array<{
    id: string;
    name: string;
    displayName: string;
    gradeId: string;
    sortOrder: number;
    isActive: boolean;
  }>>({
    queryKey: ['/api/subjects', user?.grade],
    enabled: isAuthenticated && !!user && !!user.grade,
  });

  const hasPrizes = (prizeData?.eligible) || (campaignWins.length > 0);
  
  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Signed out",
        description: "You've been successfully signed out.",
      });
      setLocation('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  // Show loading state
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  const studentName = user.firstName;
  const stats = dashboardStats || {
    totalPoints: 0,
    totalCorrect: 0,
    totalQuestions: 0,
    accuracy: 0,
    achievementCount: 0,
    streakDays: 0,
    topicProgress: []
  };
  
  // Level comes from the server's user_levels row (the same source the mobile apps read),
  // so web and iOS/Android always agree. Fall back to the shared formula only if the
  // server omitted the fields.
  const levelInfo = typeof dashboardStats?.currentLevel === "number" && typeof dashboardStats?.nextLevelXP === "number"
    ? levelInfoFromStored({
        currentLevel: dashboardStats.currentLevel,
        totalXP: dashboardStats.totalPoints,
        levelXP: dashboardStats.currentXP ?? 0,
        nextLevelXP: dashboardStats.nextLevelXP,
      })
    : computeLevel(stats.totalPoints);
  const currentLevel = levelInfo.currentLevel;
  const currentXP = levelInfo.totalXP;
  const progressPercent = levelInfo.progress * 100;
  const xpToNextLevel = levelInfo.xpToNextLevel;

  // Check profile completion and get missing fields
  const getMissingProfileFields = () => {
    const missing: string[] = [];
    
    if (!(user as any).gender) missing.push('gender');
    if (!(user as any).dateOfBirth) missing.push('date of birth');
    if (!(user as any).parentFirstName || !(user as any).parentLastName) missing.push('parent/guardian name');
    if (!(user as any).parentEmail) missing.push('parent/guardian email');
    if (!(user as any).parentPhone) missing.push('parent/guardian phone');
    
    return missing;
  };

  const missingFields = getMissingProfileFields();
  const isProfileComplete = missingFields.length === 0;

  // Smart reminder logic - show occasionally (every 3 visits)
  const shouldShowProfileReminder = () => {
    if (isProfileComplete) return false;
    
    const visitCount = parseInt(localStorage.getItem('dashboardVisitCount') || '0');
    const newVisitCount = visitCount + 1;
    localStorage.setItem('dashboardVisitCount', newVisitCount.toString());
    
    // Show every 3rd visit
    return newVisitCount % 3 === 0;
  };

  const [showProfileReminder] = useState(shouldShowProfileReminder());

  // Create a friendly message about missing fields
  const getMissingFieldsMessage = () => {
    if (missingFields.length === 0) return '';
    if (missingFields.length === 1) return `We need your ${missingFields[0]}`;
    if (missingFields.length === 2) return `We need your ${missingFields[0]} and ${missingFields[1]}`;
    
    const lastField = missingFields[missingFields.length - 1];
    const otherFields = missingFields.slice(0, -1).join(', ');
    return `We need your ${otherFields}, and ${lastField}`;
  };

  // Check for level up - only show popup once per level change
  useEffect(() => {
    if (!user || statsLoading) return;
    
    const storageKey = `lastLevel_${user.id}`;
    const shownKey = `levelUpShown_${user.id}_${currentLevel}`;
    const lastSeenLevel = localStorage.getItem(storageKey);
    const alreadyShown = localStorage.getItem(shownKey) === "true";
    
    if (lastSeenLevel && !alreadyShown) {
      const lastLevel = parseInt(lastSeenLevel);
      if (currentLevel > lastLevel) {
        setNewLevel(currentLevel);
        setShowLevelUp(true);
        try { localStorage.setItem(shownKey, "true"); } catch {}
        setTimeout(() => setShowLevelUp(false), 4000);
      }
    }
    
    try { localStorage.setItem(storageKey, currentLevel.toString()); } catch {}
  }, [currentLevel, user, statsLoading]);

  const activeLbData = dashLbMode === 'weekly' ? leaderboardData : academyLbData;
  const leaderboardEntries = activeLbData?.entries || activeLbData || [];
  const leaderboard = (Array.isArray(leaderboardEntries) ? leaderboardEntries : []).map((entry: any, index: number) => ({
    rank: entry.rank ?? index + 1,
    name: entry.user?.id === user?.id ? `${entry.user.firstName} (You)` : `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`,
    xp: entry.effectiveXp ?? entry.weeklyXp ?? entry.totalXp ?? entry.effectivePoints ?? entry.totalPoints ?? 0,
    rawXp: entry.weeklyXp ?? entry.totalXp ?? entry.totalPoints ?? 0,
    activityFactor: entry.activityFactor ?? 1.0,
    inactiveDays: entry.inactiveDays ?? 0,
    avatar: [Star, Target, Rocket, Shield, Trophy, Crown, Medal, Award][index] || User,
    isCurrentUser: entry.isCurrentUser ?? (entry.user?.id === user?.id),
    userId: entry.user?.id,
    profilePhotoUrl: entry.user?.profilePhotoUrl,
    firstName: entry.user?.firstName,
    lastName: entry.user?.lastName,
    grade: entry.user?.grade,
    lastActiveAt: entry.lastActiveAt ?? null,
  }));
  const currentUserLeaderboardEntry = leaderboard.find((s: any) => s.isCurrentUser);
  const userHasDecay = currentUserLeaderboardEntry && currentUserLeaderboardEntry.activityFactor < 1.0;

  const dashboardOtherUserIds = leaderboard.filter((s: any) => !s.isCurrentUser).map((s: any) => s.userId);
  const { data: dashboardFollowStatuses = {} } = useQuery<Record<string, string>>({
    queryKey: ['/api/social/follow-statuses', dashboardOtherUserIds.join(',')],
    queryFn: async () => {
      if (!dashboardOtherUserIds.length) return {};
      const res = await fetch(`/api/social/follow-statuses?userIds=${dashboardOtherUserIds.join(',')}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!user && dashboardOtherUserIds.length > 0,
  });

  const dashboardFollowMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await fetch(`/api/social/follow/${targetUserId}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/social/follow-statuses'] }),
    onError: () => toast({ title: "Error", description: "Could not send follow request", variant: "destructive" }),
  });

  const dashboardHandleMessage = async (targetUserId: string) => {
    try {
      const res = await fetch('/api/messages/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recipientId: targetUserId }),
      });
      if (res.ok) {
        setLocation('/messages');
      } else {
        const data = await res.json();
        toast({ title: "Can't message", description: data.error || "Failed to open conversation", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Could not open messages", variant: "destructive" });
    }
  };

  const handleViewProfile = (studentUserId: string, isCurrentUser: boolean) => {
    if (isCurrentUser) return; // Don't navigate to own profile from leaderboard
    setLocation(`/user/${studentUserId}`);
  };

  // Create a set of earned badge types for quick lookup
  const earnedBadgeTypes = new Set(
    userAchievements?.map((achievement: any) => achievement.achievementType) || []
  );

  // Get first 12 badges to display, showing earned first then unearned
  const earnedBadges = ALL_BADGES.filter(badge => earnedBadgeTypes.has(badge.type));
  const unearnedBadges = ALL_BADGES.filter(badge => !earnedBadgeTypes.has(badge.type));
  // A fresh student used to see the first row of locked badges as a wall of padlocks; show one row until they have earned a few.
  const { data: recommendations } = useQuery<RecommendationsPayload>({ queryKey: ["/api/recommendations"], staleTime: 60_000, enabled: !!user });
  const displayBadges = [...earnedBadges, ...unearnedBadges].slice(0, earnedBadges.length >= 4 ? 8 : 4);

  // "Next up" chips replace the old stack of banners. Highest value first; the prize banner stays separate.
  // Each chip can be dismissed; a dismissal hides it for 30 days (per student, per device).
  const nextUpKey = user ? `nextup_dismissed_${user.id}` : null;
  const [dismissedNextUp, setDismissedNextUp] = useState<Record<string, number>>(() => {
    try { return nextUpKey ? JSON.parse(localStorage.getItem(nextUpKey) || "{}") : {}; } catch { return {}; }
  });
  useEffect(() => {
    try { setDismissedNextUp(nextUpKey ? JSON.parse(localStorage.getItem(nextUpKey) || "{}") : {}); } catch { setDismissedNextUp({}); }
  }, [nextUpKey]);
  const dismissNextUp = (id: string) => {
    const next = { ...dismissedNextUp, [id]: Date.now() };
    setDismissedNextUp(next);
    try { if (nextUpKey) localStorage.setItem(nextUpKey, JSON.stringify(next)); } catch {}
  };
  const isDismissed = (id: string) => !!dismissedNextUp[id] && Date.now() - dismissedNextUp[id] < 30 * 86400000;
  const nextUpAll: Array<{ id: string; label: string; href: string; icon: any; bonus?: string; chip: string; iconBox: string }> = [];
  const nextUp = nextUpAll;
  if (showProfileReminder && !isProfileComplete) nextUp.push({ id: "profile", label: "Complete your profile", href: "/settings", icon: Lightbulb, chip: "bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-100 hover:bg-amber-500/20", iconBox: "bg-amber-500 text-white" });
  if (user && !user.profilePhotoUrl && !user.profilePhotoBonusAwarded) nextUp.push({ id: "photo", label: "Add a profile photo", href: "/settings", icon: Camera, bonus: "+500 XP", chip: "bg-violet-500/10 border-violet-500/30 text-violet-900 dark:text-violet-100 hover:bg-violet-500/20", iconBox: "bg-violet-500 text-white" });
  if (user?.grade === 6) nextUp.push({ id: "pep", label: "Sat PEP? Tell us how it went", href: "/pep-results", icon: Award, chip: "bg-sky-500/10 border-sky-500/30 text-sky-900 dark:text-sky-100 hover:bg-sky-500/20", iconBox: "bg-sky-500 text-white" });
  if (recommendations && !recommendations.interestsSet) nextUp.push({ id: "interests", label: "Pick what you love", href: "/profile?tab=learner", icon: Heart, chip: "bg-rose-500/10 border-rose-500/30 text-rose-900 dark:text-rose-100 hover:bg-rose-500/20", iconBox: "bg-rose-500 text-white" });
  for (let i = nextUpAll.length - 1; i >= 0; i--) if (isDismissed(nextUpAll[i].id)) nextUpAll.splice(i, 1);
  nextUp.splice(3);

  // Create a map of progress data by subject
  const progressBySubject = (dashboardStats?.subjectBreakdown || []).reduce((acc: any, subjectData: any) => {
    acc[subjectData.subject.toLowerCase()] = subjectData;
    return acc;
  }, {});

  // Normalize subject names to canonical keys for deduplication
  const toCanonical = (name: string): string => {
    const lower = name.toLowerCase().trim();
    if (lower === 'mathematics' || lower === 'math') return 'math';
    if (lower === 'language arts' || lower === 'english' || lower === 'english a') return 'english';
    if (lower === 'science') return 'science';
    if (lower === 'social studies') return 'social studies';
    if (lower === 'ability test') return 'ability test';
    return lower;
  };

  const toDisplayName = (canonical: string): string => {
    const map: Record<string, string> = {
      'math': 'Mathematics',
      'english': 'Language Arts',
      'science': 'Science',
      'social studies': 'Social Studies',
      'ability test': 'Ability Test',
    };
    return map[canonical] || canonical.charAt(0).toUpperCase() + canonical.slice(1);
  };

  const sortOrderMap: Record<string, number> = {
    'math': 1, 'english': 2, 'science': 3, 'social studies': 4, 'ability test': 5,
  };

  // Build subjects array merging question-derived subjects AND subjects the student has progress in
  const subjectMap = new Map<string, { key: string; name: string; sortOrder: number; progressKeys: string[] }>();

  for (const subject of subjectsData) {
    const canonical = toCanonical(subject.name);
    if (!subjectMap.has(canonical)) {
      subjectMap.set(canonical, {
        key: canonical,
        name: subject.displayName,
        sortOrder: subject.sortOrder,
        progressKeys: [subject.name.toLowerCase()],
      });
    }
  }

  for (const subjectData of (dashboardStats?.subjectBreakdown || [])) {
    const canonical = toCanonical(subjectData.subject);
    if (!subjectMap.has(canonical)) {
      // A subject the student has XP in but which is NOT taught at their current grade does not
      // get a tile. Carrying it over is why a Grade 11 student saw Mathematics, Language Arts,
      // Science, Social Studies and Ability Test: those are PEP subjects they earned XP in years
      // earlier, and Ability Test is a PEP paper that does not exist at CSEC at all. The tile is
      // an invitation to practise, and there is nothing at this grade to practise.
      continue;
    } else {
      const existing = subjectMap.get(canonical)!;
      if (!existing.progressKeys.includes(subjectData.subject.toLowerCase())) {
        existing.progressKeys.push(subjectData.subject.toLowerCase());
      }
    }
  }

  const subjects = Array.from(subjectMap.values())
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(subject => {
      const metadata = getTopicMetadata(subject.key, 'subject');
      // Sum progress across all aliases (e.g. "math" + "mathematics")
      let points = 0;
      let fatigue = 0;
      for (const pk of subject.progressKeys) {
        const pd = progressBySubject[pk];
        if (pd) {
          points += pd.points || 0;
          fatigue = Math.max(fatigue, pd.fatigue || 0);
        }
      }
      // Also check the canonical key itself
      const canonicalProgress = progressBySubject[subject.key];
      if (canonicalProgress && !subject.progressKeys.includes(subject.key)) {
        points += canonicalProgress.points || 0;
        fatigue = Math.max(fatigue, canonicalProgress.fatigue || 0);
      }
      
      // Same XP -> level rule as the overall level (shared/xp.ts), applied to subject XP.
      const subjectLevelInfo = computeLevel(points);

      return {
        key: subject.key,
        name: subject.name,
        icon: metadata.icon,
        color: metadata.color,
        progress: Math.round(subjectLevelInfo.progress * 100),
        level: subjectLevelInfo.currentLevel,
        xpToNext: subjectLevelInfo.xpToNextLevel,
        points: points,
        fatigue: fatigue
      };
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
      <PaymentGraceBanner />
      <PlatformBanner />
      {/* Level Up Overlay */}
      <AnimatePresence>
        {showLevelUp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            data-testid="level-up-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <Card className="w-full sm:w-96 max-w-[90vw] rounded-xl border shadow-sm">
                <CardContent className="p-6 md:p-8 text-center">
                  <Illustration name="owl-cheer" size={140} className="mx-auto" />
                  <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight">Level up!</h2>
                  <p className="mt-1 text-base font-semibold text-primary tabular-nums">You reached Level {newLevel}</p>
                  <p className="mt-2 text-sm text-muted-foreground">Keep going, Owlbert is impressed.</p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Header */}
      <div className="relative bg-[#4C1D95] text-white px-6 sm:px-8 py-6 sm:py-8 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        
        <div className="container max-w-[1400px] mx-auto relative z-10">
          <div className="flex justify-end mb-4">
            {/* Logout Button - Top Right */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/20 gap-2"
              data-testid="button-logout"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col items-center justify-center gap-5 text-center"
          >
            {/* Owlbert - the one illustration voice */}
            <div className="rounded-full bg-white/10 ring-1 ring-white/15 p-4 md:p-5">
              <Illustration name="owl-wave" size={140} className="hidden md:block" />
              <Illustration name="owl-wave" size={110} className="md:hidden" />
            </div>

            {/* Greeting */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-1"
            >
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="text-welcome">
                Welcome back, {studentName}!
              </h1>
              <p className="text-sm md:text-base text-white/80" data-testid="text-motivation">
                Ready to learn something today?
              </p>
            </motion.div>

            {/* Level & progress */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center gap-2 w-full max-w-xs sm:max-w-sm"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-lg md:text-xl font-semibold" data-testid="text-current-level">Level {currentLevel}</span>
                <span className="text-sm text-white/70 tabular-nums" data-testid="text-total-xp">{currentXP.toLocaleString()} XP</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-white"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  data-testid="progress-level"
                />
              </div>
              <p className="text-xs text-white/70 tabular-nums" data-testid="text-progress">
                {xpToNextLevel} XP to Level {currentLevel + 1}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="container max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Quick Start Guide for New Users */}
        <QuickStartGuide
          user={user}
          dashboardStats={dashboardStats}
          userAchievements={userAchievements || []}
          readingStats={readingStats}
        />

        {/* Prize Notification Banner */}
        {hasPrizes && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="rounded-xl border-destructive/40 bg-destructive/5" data-testid="card-prize-notification">
              <CardContent className="p-4 md:p-5 flex items-center gap-3">
                <div className="flex items-center gap-2 md:gap-3 flex-1">
                  <img src="/giftme-logo.webp" alt="Giftme" className="w-8 h-8 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm md:text-base flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-red-500" />
                      {prizeData?.eligible && prizeData.prizeAmountJMD
                        ? `You won a J$${prizeData.prizeAmountJMD.toLocaleString()} ${prizeData.merchantName || 'Giftme'} gift card`
                        : `You have ${(prizeData?.eligible ? 1 : 0) + campaignWins.length} Giftme smart card${(prizeData?.eligible ? 1 : 0) + campaignWins.length === 1 ? '' : 's'} waiting`}
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {prizeData?.milestone ? `For finishing ${prizeData.milestone} tests. Tap Claim and it is sent to a phone by text.` : 'Tap to claim your rewards now'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    apiRequest('POST', '/api/notifications/mark-prize-read').then(() => {
                      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
                      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
                    }).catch(() => {});
                    setLocation('/claim-prize');
                  }}
                  size="sm"
                  className="flex-shrink-0 rounded-lg text-white hover:opacity-90"
                  style={{ backgroundColor: '#DC2626' }}
                  data-testid="button-claim-prizes"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Claim Now
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Next up: one quiet strip instead of a stack of banners */}
        {nextUp.length > 0 && (
          <Card data-testid="card-next-up">
            <CardContent className="p-3 md:p-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mr-1">Next up</span>
              {nextUp.map((item) => (
                <div key={item.id} className={`group inline-flex items-center rounded-full border pl-1.5 pr-1 py-1 text-sm font-medium transition-colors ${item.chip}`} data-testid={`next-up-${item.id}`}>
                  <button type="button" onClick={() => setLocation(item.href)} className="inline-flex items-center gap-2 pr-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center ${item.iconBox}`}><item.icon className="w-3.5 h-3.5" /></span>
                    <span>{item.label}</span>
                    {item.bonus ? <span className="text-[11px] font-bold opacity-80">{item.bonus}</span> : null}
                    <ChevronRight className="w-3.5 h-3.5 opacity-60 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); dismissNextUp(item.id); }}
                    className="w-6 h-6 rounded-full flex items-center justify-center opacity-50 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
                    aria-label={`Dismiss: ${item.label}`}
                    title="Not now"
                    data-testid={`next-up-dismiss-${item.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Live Classes CTA: feature-flag gated, only renders when summer classes are live */}
        <SummerClassesTile />



        {/* Main Content Grid - 65% / 35% Split on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.54fr] gap-4 md:gap-6">
          
          {/* Subjects Card - Left column on desktop, first on mobile */}
          <div className="order-1 lg:col-start-1 lg:row-start-1">
            <Card className="rounded-xl" data-testid="card-subjects">
              <CardHeader className="p-4 md:p-5 pb-3">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg font-semibold">
                  <Book className="w-5 h-5 text-primary" />
                  Your Subjects
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-5 pt-0">
                <div className="grid gap-4 sm:grid-cols-2">
                  {subjects.map((subject) => {
                    const IconComponent = subject.icon;
                    const xpToNext = subject.xpToNext;
                    const isAlmostThere = xpToNext <= 50 && subject.points > 0;
                    const style = SUBJECT_STYLES[subject.key] ?? DEFAULT_SUBJECT_STYLE;
                    const slug = subject.name.toLowerCase().replace(' ', '-');
                    return (
                      <div
                        key={subject.name}
                        className={`group relative overflow-hidden border rounded-xl p-4 md:p-5 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${style.card}`}
                        data-testid={`card-subject-${slug}`}
                        onClick={() => setLocation(`/learning?subject=${encodeURIComponent(subject.key)}`)}
                      >
                        <TileBackdrop theme={SUBJECT_THEME[subject.key] ?? "ability"} className={SUBJECT_DOODLE[subject.key] ?? "text-primary"} />
                        <div className="tile-shine" />
                        <div className="relative">
                        <div className="flex items-center gap-3">
                          <div className={`sticker-bob w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${style.sticker} ${STICKER_TILT[subjects.indexOf(subject) % STICKER_TILT.length]}`} style={{ ["--sticker-rot" as any]: STICKER_ROT[subjects.indexOf(subject) % STICKER_ROT.length] }}>
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-bold text-base md:text-lg leading-tight truncate">{subject.name}</h3>
                            <p className={`text-xs font-medium tabular-nums ${style.text}`}>{subject.points.toLocaleString()} XP</p>
                          </div>
                          <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold tabular-nums ${style.chip}`} data-testid={`badge-level-${slug}`}>
                            Lvl {subject.level}
                          </span>
                        </div>

                        {(() => { if (!isPrimaryExam) return null; const cd = countdownForSubject(pepWindows, subject.key); return cd ? <PepCountdownChip countdown={cd} tint={style.chip} className="mt-3" testId={`pep-countdown-${slug}`} /> : null; })()}

                        <div className="mt-3 space-y-1.5">
                          <div className={`w-full h-2 rounded-full overflow-hidden ${style.track}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${style.bar}`}
                              style={{ width: `${subject.progress}%` }}
                            />
                          </div>
                          <p className={`text-xs font-medium ${style.text}`}>
                            {isAlmostThere
                              ? `Almost there. ${xpToNext} XP to Level ${subject.level + 1}`
                              : `${xpToNext} XP to Level ${subject.level + 1}`}
                          </p>
                        </div>

                        <Button
                          size="sm"
                          className={`w-full mt-3 rounded-lg font-semibold border-0 ${style.button}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/learning?subject=${encodeURIComponent(subject.key)}`);
                          }}
                          data-testid={`button-continue-${slug}`}
                        >
                          {subject.points > 0 ? 'Keep going' : 'Start'}
                        </Button>
                        </div>
                      </div>
                    );
                  })}
                  <ForYouTile />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard - Right column on desktop, second on mobile (after Subjects) */}
          <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-3 space-y-4 md:space-y-6">
            {userHasDecay && currentUserLeaderboardEntry && (
              <Card className="border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20" data-testid="card-decay-warning">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <TrendingDown className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Your XP is dropping!
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        You've been inactive for {currentUserLeaderboardEntry.inactiveDays} days.
                        Your score is reduced by {Math.round((1 - currentUserLeaderboardEntry.activityFactor) * 100)}%.
                        Take a quiz to restore your full score!
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 border-amber-500/50 text-amber-700 dark:text-amber-300"
                        onClick={() => setLocation('/learning')}
                        data-testid="button-restore-xp"
                      >
                        <Flame className="w-4 h-4 mr-1" />
                        Start a Quiz
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="rounded-xl" data-testid="card-quick-stats">
              <CardHeader className="p-4 md:p-5 pb-3">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg font-semibold">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  This Week
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-5 pt-0 space-y-2">
                <div className="group relative overflow-hidden flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20" data-testid="stat-lessons-completed">
                  <TileBackdrop theme="active" className="text-emerald-700" count={6} opacity={0.14} />
                  <div className="relative w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 -rotate-3 shadow-sm">
                    <Zap className="w-6 h-6 bolt-pulse" />
                  </div>
                  <div className="relative min-w-0">
                    <p className="font-bold text-lg leading-tight tabular-nums text-emerald-900 dark:text-emerald-100" data-testid="text-lessons-count">
                      {weeklyStats?.daysActive ?? 0} {(weeklyStats?.daysActive ?? 0) === 1 ? 'day' : 'days'}
                    </p>
                    <p className="text-xs font-medium text-emerald-900/70 dark:text-emerald-200/80">{(weeklyStats?.daysActive ?? 0) === 0 ? "Practise today to start the count" : "Active this week"}</p>
                  </div>
                </div>

                <div className="group relative overflow-hidden flex items-center gap-3 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20" data-testid="stat-streak">
                  <TileBackdrop theme="streak" className="text-orange-700" count={6} opacity={0.14} />
                  <div className="relative w-11 h-11 rounded-xl bg-orange-500 text-white flex items-center justify-center flex-shrink-0 rotate-2 shadow-sm">
                    <Flame className="w-6 h-6 flame-flicker" />
                  </div>
                  <div className="relative min-w-0">
                    <p className="font-bold text-lg leading-tight tabular-nums text-orange-900 dark:text-orange-100" data-testid="text-streak-count">
                      {weeklyStats?.currentStreak ?? 0} day streak
                    </p>
                    <p className="text-xs font-medium text-orange-900/70 dark:text-orange-200/80">{(weeklyStats?.currentStreak ?? 0) === 0 ? "One quiz lights the flame" : "Don't let it go out"}</p>
                  </div>
                </div>

                <div className="group relative overflow-hidden flex items-center gap-3 p-3 rounded-xl bg-violet-500/10 border border-violet-500/20" data-testid="stat-weekly-xp">
                  <TileBackdrop theme="xp" className="text-violet-700" count={6} opacity={0.14} />
                  <div className="relative w-11 h-11 rounded-xl bg-violet-500 text-white flex items-center justify-center flex-shrink-0 -rotate-2 shadow-sm">
                    <Brain className="w-6 h-6" />
                    <Sparkles className="twinkle absolute -top-1.5 -right-1.5 w-3.5 h-3.5 text-[#ffd66b]" />
                    <Sparkles className="twinkle absolute -bottom-1 -left-1.5 w-2.5 h-2.5 text-[#ffd66b]" style={{ animationDelay: "-1.1s" }} />
                  </div>
                  <div className="relative min-w-0">
                    <p className="font-bold text-lg leading-tight tabular-nums text-violet-900 dark:text-violet-100" data-testid="text-weekly-xp">
                      {(weeklyStats?.weeklyXP ?? 0).toLocaleString()} XP
                    </p>
                    <p className="text-xs font-medium text-violet-900/70 dark:text-violet-200/80">Earned this week</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Study plan, compact: it also lives in the nav, so a small card is enough here */}
            <Card className="group relative overflow-hidden rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 bg-primary/10 border-primary/25" onClick={() => setLocation('/study-plan')} data-testid="card-study-plan">
              <TileBackdrop theme="plan" className="text-primary" count={6} opacity={0.12} />
              <div className="tile-shine" />
              <CardContent className="relative p-3 md:p-4 flex items-center gap-3">
                <div className="relative w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 rotate-2 shadow-sm">
                  <span className="ring-ping absolute inset-0 rounded-xl border-2 border-primary/60" aria-hidden="true" />
                  <Target className="w-6 h-6 relative" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Smart Study Planner</p>
                  <p className="text-xs text-muted-foreground">Today's topics, picked for you</p>
                </div>
                <Button size="sm" variant="secondary" className="flex-shrink-0 rounded-lg">View Plan</Button>
              </CardContent>
            </Card>
            <Card className="rounded-xl overflow-hidden" data-testid="card-leaderboard">
              {/* Leaderboard toggle */}
              <div className="px-4 py-2.5 flex items-center justify-between bg-muted/80 border-b">
                <div className="flex items-center bg-muted rounded-full p-0.5">
                  <button
                    onClick={() => setDashLbMode('weekly')}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${dashLbMode === 'weekly' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'}`}
                  >
                    <Flame className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                    This Week
                  </button>
                  <button
                    onClick={() => setDashLbMode('academy')}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${dashLbMode === 'academy' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'}`}
                  >
                    <Trophy className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                    Academy
                  </button>
                </div>
                <button onClick={() => setLocation('/leaderboard')} className="text-muted-foreground text-xs hover:text-foreground transition-colors">
                  {dashLbMode === 'weekly' ? 'Resets Sunday' : 'All-Time'} →
                </button>
              </div>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg font-semibold">
                    <Trophy className="w-5 h-5 text-primary" />
                    Leaderboard
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLeaderboardInfo(!showLeaderboardInfo)}
                    className="text-muted-foreground gap-1"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline text-xs">How it works</span>
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showLeaderboardInfo ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showLeaderboardInfo && (
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 space-y-2 text-sm">
                    <p className="text-muted-foreground">
                      Earn XP by answering questions. <strong className="text-foreground">Stay active to keep your score!</strong>
                    </p>
                  </div>
                )}
                {leaderboard.length === 0 && (
                  <StudentEmptyState
                    illustration="owl-trophy"
                    title="No one's on the board yet"
                    hint="Answer a few questions and your name lands here."
                    size={100}
                  />
                )}
                <div className="space-y-1">
                  {leaderboard.slice(0, 20).map((student, index) => {
                    const trend = getDashboardTrend(student.inactiveDays, student.activityFactor);
                    const hasDecay = student.activityFactor < 1.0;
                    return (
                    <div 
                      key={student.rank} 
                      className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-lg transition-all duration-200 ${
                        student.isCurrentUser 
                          ? 'bg-primary/8 border border-primary/15' 
                          : 'hover:bg-muted/60'
                      }`}
                      data-testid={`leaderboard-${student.rank}`}
                    >
                      <span className={`w-6 text-center text-sm font-bold tabular-nums ${
                        student.rank === 1 ? 'text-yellow-500' :
                        student.rank === 2 ? 'text-gray-400' :
                        student.rank === 3 ? 'text-orange-400' :
                        'text-muted-foreground'
                      }`}>
                        {student.rank}
                      </span>
                      
                      <div className="relative flex-shrink-0">
                        <Avatar
                          className={`h-9 w-9 md:h-10 md:w-10 ${student.profilePhotoUrl ? 'cursor-pointer hover:ring-2 hover:ring-primary transition-all' : ''}`}
                          onClick={() => student.profilePhotoUrl && setViewingPhoto({ url: student.profilePhotoUrl, name: student.name })}
                        >
                          <AvatarImage
                            src={student.profilePhotoUrl || ''}
                            alt={`${student.firstName} ${student.lastName}`}
                            data-testid={`img-leaderboard-avatar-${student.userId}`}
                          />
                          <AvatarFallback className="bg-muted text-muted-foreground text-xs font-medium">
                            {student.firstName?.[0]}{student.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-1 -right-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <DashboardTrendArrow trend={trend} />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{trend.label}</p>
                              <p className="text-xs">{trend.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {student.isCurrentUser ? (
                            <span className="font-semibold text-sm text-primary truncate">
                              {student.name}
                            </span>
                          ) : (
                            <button
                              data-testid={`button-view-profile-${student.userId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewProfile(student.userId, student.isCurrentUser);
                              }}
                              className="font-semibold text-sm hover:text-primary hover:underline transition-colors truncate text-left"
                            >
                              {student.name}
                            </button>
                          )}
                          {hasDecay && (
                            <span className={`text-xs font-medium flex-shrink-0 ${trend.arrowColor}`}>
                              -{Math.round((1 - student.activityFactor) * 100)}%
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {student.xp.toLocaleString()} XP{dashLbMode === 'academy' && student.grade ? ` · Grade ${student.grade}` : ''}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!student.isCurrentUser && (() => {
                          const fs = dashboardFollowStatuses[student.userId] || 'none';
                          if (fs === 'accepted') {
                            return (
                              <Button size="icon" variant="ghost" className="text-muted-foreground h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); dashboardHandleMessage(student.userId); }}
                                data-testid={`button-message-${student.userId}`}>
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                            );
                          }
                          if (fs === 'pending') {
                            return (
                              <Button size="icon" variant="ghost" className="text-muted-foreground h-8 w-8" disabled>
                                <ClockIcon className="w-4 h-4" />
                              </Button>
                            );
                          }
                          return (
                            <Button size="icon" variant="ghost" className="text-muted-foreground h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); dashboardFollowMutation.mutate(student.userId); }}
                              disabled={dashboardFollowMutation.isPending}
                              data-testid={`button-follow-${student.userId}`}>
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          );
                        })()}
                        {student.rank <= 3 && (
                          <Medal className={`w-4 h-4 ${
                            student.rank === 1 ? 'text-yellow-500' :
                            student.rank === 2 ? 'text-gray-400' :
                            'text-orange-400'
                          }`} />
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
                
                <Button
                  onClick={() => setLocation('/leaderboard')}
                  variant="outline"
                  className="w-full mt-4 rounded-lg font-medium"
                  data-testid="button-view-full-leaderboard"
                >
                  View Full Leaderboard
                </Button>
              </CardContent>
            </Card>

            <GetTheAppCard audience="student" />

            {/* Online Now widget */}
            {allFriends.length > 0 && (
              <Card className="rounded-xl">
                <CardHeader className="p-4 md:p-5 pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                    </span>
                    Online Now
                    {onlineFriends.length > 0 && (
                      <span className="ml-auto text-xs font-normal text-muted-foreground">{onlineFriends.length} online</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {onlineFriends.length === 0 ? (
                    <StudentEmptyState
                      illustration="owl-sleepy"
                      title="Nobody's around right now"
                      hint="Owlbert will keep an eye out. Check back in a bit."
                      size={80}
                      className="py-3"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {onlineFriends.map(friend => (
                        <button
                          key={friend.id}
                          onClick={() => setLocation(`/user/${friend.id}`)}
                          className="flex flex-col items-center gap-1 group"
                        >
                          <div className="relative">
                            <Avatar className="h-10 w-10 group-hover:ring-2 group-hover:ring-primary transition-all">
                              <AvatarImage src={friend.profilePhotoUrl || ""} />
                              <AvatarFallback className="bg-primary/10 text-xs font-semibold">
                                {friend.firstName?.[0]}{friend.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <OnlineDot isOnline={true} className="absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-background" />
                          </div>
                          <p className="text-[11px] text-muted-foreground max-w-[48px] truncate">{friend.firstName}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Prize ladder: the Giftme milestones, where this student stands, and any card waiting */}
            {(prizeData?.tiers?.length ?? 0) > 0 && (() => {
              const tiers = prizeData!.tiers!;
              const done = prizeData?.testsCompleted ?? 0;
              const next = prizeData?.nextMilestone ?? null;
              const prev = [...tiers.map(t => t.milestone)].filter(m => m <= done).pop() ?? 0;
              const pct = next ? Math.min(100, Math.round(((done - prev) / (next - prev)) * 100)) : 100;
              return (
                <Card className="rounded-xl border-amber-400/60 bg-amber-500/5" data-testid="card-prize-ladder">
                  <CardHeader className="p-4 md:p-5 pb-3">
                    <CardTitle className="flex items-center gap-3 text-base font-semibold">
                      <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 -rotate-2 shadow-sm"><Gift className="w-5 h-5" /></div>
                      <span>Prizes</span>
                      <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">Powered by <img src="/giftme-logo.webp" alt="Giftme" className="h-5" /></span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {prizeData?.eligible && (
                      <div className="rounded-lg bg-amber-400 text-amber-950 p-3 flex items-center gap-3" data-testid="prize-ladder-win">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold leading-tight">You won a J${(prizeData.prizeAmountJMD ?? 0).toLocaleString()} {prizeData.merchantName || 'Giftme'} card</p>
                          <p className="text-xs">Claim it and it is sent to a parent's phone by text.</p>
                        </div>
                        <Button size="sm" className="rounded-lg bg-amber-950 text-amber-50 hover:bg-amber-900 shrink-0" onClick={() => setLocation('/claim-prize')}>Claim</Button>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-medium text-muted-foreground">{done.toLocaleString()} {done === 1 ? 'test' : 'tests'} finished</span>
                        <span className="font-semibold tabular-nums">{next ? `${Math.max(0, next - done)} to the next prize` : 'Every prize milestone reached'}</span>
                      </div>
                      <div className="h-2 rounded-full bg-amber-500/15 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div className="h-full rounded-full bg-amber-500 transition-all duration-700" style={{ width: `${pct}%` }} /></div>
                    </div>
                    <ul className="space-y-2">
                      {tiers.map((t) => {
                        const reached = done >= t.milestone;
                        const isNext = t.milestone === next;
                        const rules = [t.winProbability >= 100 ? 'Guaranteed' : `1 in ${Math.max(1, Math.round(100 / t.winProbability))} chance`, t.minAvgScore ? `${t.minAvgScore}%+ accuracy` : null].filter(Boolean).join(' · ');
                        return (
                          <li key={t.milestone} className={`flex items-center gap-3 rounded-lg border p-2.5 ${isNext ? 'border-amber-400/70 bg-card' : reached ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border bg-card/60'}`} data-testid={`prize-tier-${t.milestone}`}>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold ${reached ? 'bg-emerald-500 text-white' : isNext ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>{reached ? <Check className="w-4 h-4" /> : t.milestone}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold leading-tight">J${t.prizeAmountJMD.toLocaleString()} {t.merchantName || 'Giftme'} card</p>
                              <p className="text-[11px] text-muted-foreground truncate">Finish {t.milestone} tests · {rules}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <Button variant="ghost" size="sm" className="w-full rounded-lg text-primary" onClick={() => setLocation('/prizes')} data-testid="button-prize-ladder-more">How prizes work<ArrowRight className="w-4 h-4 ml-1" /></Button>
                  </CardContent>
                </Card>
              );
            })()}

          </div>

          {/* Badges - Left column on desktop, third on mobile (after Leaderboard) */}
          <div className="order-3 lg:col-start-1 lg:row-start-2 space-y-4 md:space-y-6">
            {/* Interactive Lessons */}
            {lessonStats && lessonStats.total > 0 && (() => {
              // Was three grey stat boxes, a progress bar and a generic button. Grey-on-grey is a
              // failure state per DESIGN_SYSTEM.md, "0 In Progress" is a number nobody can act on,
              // and a 100% bar tells a finished student there is nothing here. Lead with the
              // lesson instead: its subject colour, its name, what it costs and what it pays.
              const key = nextLesson ? toCanonical(nextLesson.subject) : "";
              const style = SUBJECT_STYLES[key] ?? DEFAULT_SUBJECT_STYLE;
              const allDone = !nextLesson;
              return (
                <Card className="rounded-xl overflow-hidden" data-testid="card-lessons">
                  <CardHeader className="p-4 md:p-5 pb-3">
                    <CardTitle className="flex items-center justify-between gap-2 text-base md:text-lg font-semibold">
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary" />
                        Interactive Lessons
                      </span>
                      <span className="text-xs font-medium text-muted-foreground tabular-nums">
                        {lessonStats.completed}/{lessonStats.total} done
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 md:p-5 pt-0 space-y-3">
                    {allDone ? (
                      <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-center">
                        <Illustration name="owl-trophy" size={72} className="mx-auto" />
                        <p className="mt-2 font-semibold text-emerald-900 dark:text-emerald-100">
                          Every lesson done
                        </p>
                        <p className="text-xs text-emerald-900/70 dark:text-emerald-200/80">
                          {lessonStats.xpEarned.toLocaleString()} XP earned. More are on the way.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={() => setLocation(`/lessons/${nextLesson!.slug}`)}
                        className={`w-full text-left rounded-xl border p-4 transition-transform hover:-translate-y-0.5 ${style.card}`}
                        data-testid="button-lesson-next"
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl -rotate-2 ${style.sticker}`}>
                            <Sparkles className="h-5 w-5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block text-[11px] font-bold uppercase tracking-wide ${style.text}`}>
                              {resuming ? "Pick up where you left off" : nextLesson!.subject}
                            </span>
                            <span className="block font-bold leading-snug text-foreground">
                              {nextLesson!.title}
                            </span>
                            <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${style.chip}`}>
                                {nextLesson!.estimatedMinutes} min
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${style.chip}`}>
                                +{nextLesson!.xpReward} XP
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${style.chip}`}>
                                {nextLesson!.topic}
                              </span>
                            </span>
                          </span>
                        </div>
                        <span className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-bold ${style.button}`}>
                          {resuming ? "Continue" : "Start this lesson"}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </button>
                    )}

                    <button
                      onClick={() => setLocation('/lessons')}
                      className="w-full text-center text-xs font-semibold text-muted-foreground hover:text-foreground"
                      data-testid="button-lessons-library"
                    >
                      Browse all {lessonStats.total} lesson{lessonStats.total === 1 ? "" : "s"}
                    </button>
                  </CardContent>
                </Card>
              );
            })()}

            {isPrimaryExam && (
              <PerformanceTaskTile grade={Number(user?.grade)} enabled={isAuthenticated && !!user} />
            )}

            <Card className="rounded-xl" data-testid="card-badges">
              <CardHeader className="p-4 md:p-5 pb-3">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg font-semibold">
                  <Award className="w-5 h-5 text-primary" />
                  Your Badges
                </CardTitle>
                <p className="text-xs font-medium text-muted-foreground tabular-nums">{earnedBadgeTypes.size} of {ALL_BADGES.length} earned</p>
              </CardHeader>
              <CardContent className="p-4 md:p-5 pt-0">
                {earnedBadgeTypes.size === 0 ? (
                  <StudentEmptyState
                    illustration="owl-lock"
                    title="Your badge shelf is empty"
                    hint="Answer a few questions and the first one unlocks itself."
                    action={<Button className="rounded-lg" onClick={() => setLocation('/learning')} data-testid="button-badges-start">Start a quiz</Button>}
                  />
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {displayBadges.map((badge) => {
                      const isEarned = earnedBadgeTypes.has(badge.type);
                      return (
                        <div
                          key={badge.type}
                          className={`rounded-xl border p-4 text-center transition-all duration-200 ${isEarned ? 'bg-card border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : 'bg-muted/40 border-transparent cursor-default'}`}
                          data-testid={`badge-${badge.type}`}
                        >
                          <BadgeArt type={badge.type} name={badge.name} earned={isEarned} size={72} className="mx-auto mb-2" />
                          <h4 className={`font-semibold text-sm mb-0.5 ${isEarned ? '' : 'text-muted-foreground/70'}`}>{badge.name}</h4>
                          <p className={`text-xs ${isEarned ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>{badge.description}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Button
                  onClick={() => setLocation('/badges')}
                  variant="outline"
                  className="w-full mt-4 rounded-lg font-medium"
                  data-testid="button-view-all-badges"
                >
                  View All Badges ({ALL_BADGES.length} total)
                </Button>
              </CardContent>
            </Card>



            {/* Reading Progress Card */}
            {readingStats && readingStats.totalMaterials > 0 && (
              <Card className="rounded-xl" data-testid="card-reading">
                <CardHeader className="p-4 md:p-5 pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg font-semibold">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Reading Library
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-5 pt-0">
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 rounded-lg bg-muted/40">
                        <p className="text-xl font-semibold tabular-nums">{readingStats.completed}</p>
                        <p className="text-xs text-muted-foreground">Completed</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/40">
                        <p className="text-xl font-semibold tabular-nums">{readingStats.inProgress}</p>
                        <p className="text-xs text-muted-foreground">In Progress</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/40">
                        <p className="text-xl font-semibold tabular-nums">{readingStats.totalMaterials - readingStats.completed - readingStats.inProgress}</p>
                        <p className="text-xs text-muted-foreground">Not Started</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-medium text-muted-foreground">
                        <span>Overall Progress</span>
                        <span className="tabular-nums">{Math.round((readingStats.completed / readingStats.totalMaterials) * 100)}%</span>
                      </div>
                      <Progress value={(readingStats.completed / readingStats.totalMaterials) * 100} className="h-1.5" />
                    </div>

                    <Button
                      onClick={() => setLocation('/reading')}
                      className="w-full rounded-lg font-medium"
                      data-testid="button-reading-library"
                    >
                      Open Reading Library
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

      </div>

      <ProfilePhotoViewer
        url={viewingPhoto?.url ?? null}
        name={viewingPhoto?.name ?? ''}
        onClose={() => setViewingPhoto(null)}
      />

    </div>
  );
};

export default Dashboard;